import type {
  StatementNodeAttributes,
  StatementNodeData,
  StatementNodeType,
  StatementSegment,
  StatementSentenceRow,
} from "./types";

let rowSequence = 0;
const nextLocalRowId = (): string => {
  rowSequence += 1;
  return `row-local-${rowSequence}`;
};

const parseBoolean = (value: string | undefined): boolean | undefined => {
  if (value !== undefined && ["true", "1", "yes"].includes(value.toLowerCase()))
    return true;
  if (value !== undefined && ["false", "0", "no"].includes(value.toLowerCase()))
    return false;
  return undefined;
};

const trimOuterWhitespace = (value: string): string =>
  value.replace(/^\s+|\s+$/g, "");

const withRowId = (
  row: Omit<StatementSentenceRow, "rowId">,
  rowId: string,
): StatementSentenceRow => ({
  rowId,
  ...row,
});

export const segmentsToSentenceRows = (
  segments: StatementSegment[],
): StatementSentenceRow[] => {
  const rows: StatementSentenceRow[] = [];

  segments.forEach((segment, segmentIndex) => {
    if (segment.kind === "node") {
      rows.push(
        withRowId(
          {
            sourceKind: "node",
            text: segment.node.text,
            nodeType: segment.node.type,
            id: segment.node.id,
            code: segment.node.code,
            groupId: segment.node.groupId,
            hasVariable: segment.node.hasVariable,
            variantId: segment.node.variantId,
            isEdited: segment.node.isEdited,
            attributes: { ...segment.node.attributes },
          },
          `row-node-${segmentIndex}-${segment.node.id || segment.node.groupId || segment.node.code || "plain"}`,
        ),
      );
      return;
    }

    const text = trimOuterWhitespace(segment.text);
    if (!text) return;

    rows.push(
      withRowId(
        {
          sourceKind: "text",
          text,
          attributes: {},
        },
        `row-text-${segmentIndex}`,
      ),
    );
  });

  return rows;
};

const inferNodeType = (row: StatementSentenceRow): StatementNodeType => {
  if (row.nodeType) return row.nodeType;
  if (row.code) return "GlossaryStatementNode";
  return "StatementNode";
};

const toNodeData = (row: StatementSentenceRow): StatementNodeData => {
  const type = inferNodeType(row);
  const attributes: StatementNodeAttributes = { ...row.attributes };
  // Preserve incomplete historical identities. Defaults belong to an explicit
  // new assignment, never to parsing/serializing somebody's saved reference.
  for (const key of ["id", "code", "groupId", "variantId"] as const) {
    if (row[key] !== undefined) attributes[key] = row[key]!;
    else delete attributes[key];
  }
  for (const key of ["isEdited", "hasVariable"] as const) {
    if (
      typeof row[key] === "boolean" &&
      parseBoolean(attributes[key]) !== row[key]
    )
      attributes[key] = String(row[key]);
  }
  return {
    type,
    text: row.text,
    attributes,
    id: row.id,
    code: row.code,
    groupId: row.groupId,
    hasVariable: row.hasVariable,
    variantId: row.variantId,
    isEdited: row.isEdited,
  };
};

const separatorBetween = (
  prev: StatementSentenceRow,
  next: StatementSentenceRow,
): string => {
  if (prev.text.endsWith("\n") || next.text.startsWith("\n")) return "";
  return " ";
};

export const sentenceRowsToSegments = (
  rows: StatementSentenceRow[],
): StatementSegment[] => {
  const normalized = rows;
  const segments: StatementSegment[] = [];

  normalized.forEach((row, index) => {
    const isNode = row.sourceKind === "node" || !!row.code || !!row.nodeType;

    if (isNode) {
      segments.push({
        kind: "node",
        node: toNodeData(row),
      });
    } else {
      segments.push({
        kind: "text",
        text: row.text,
      });
    }

    const next = normalized[index + 1];
    if (!next) return;

    const separator = separatorBetween(row, next);
    if (separator) segments.push({ kind: "text", text: separator });
  });

  return segments;
};

export const sentenceRowsToPlainText = (
  rows: StatementSentenceRow[],
): string => {
  return sentenceRowsToSegments(rows)
    .map((segment) =>
      segment.kind === "text" ? segment.text : segment.node.text,
    )
    .join("");
};

export const createEmptySentenceRow = (): StatementSentenceRow =>
  withRowId(
    {
      sourceKind: "node",
      text: "",
      nodeType: "StatementNode",
      attributes: {
        isEdited: "true",
      },
      isEdited: true,
    },
    nextLocalRowId(),
  );

export const parseRowEditedFlag = (row: StatementSentenceRow): boolean => {
  if (typeof row.isEdited === "boolean") return row.isEdited;
  return parseBoolean(row.attributes.isEdited) === true;
};
